import { useState, useCallback } from "react";

// ── AHP ENGINE ──────────────────────────────────────────────────────────────
const RI_TABLE = [0,0,0.58,0.9,1.12,1.24,1.32,1.41,1.45,1.49];

function ahp(mat, n) {
  const colSum = Array(n).fill(0);
  for (let i=0;i<n;i++) for (let j=0;j<n;j++) colSum[j]+=mat[i*n+j];
  const norm = mat.map((v,idx)=>v/colSum[idx%n]);
  const w = Array(n).fill(0);
  for (let i=0;i<n;i++) for (let j=0;j<n;j++) w[i]+=norm[i*n+j];
  for (let i=0;i<n;i++) w[i]/=n;
  let lmax=0;
  for (let j=0;j<n;j++){
    let wv=0; for(let i=0;i<n;i++) wv+=mat[j*n+i]*w[i];
    lmax+=wv/w[j];
  }
  lmax/=n;
  const ci=(lmax-n)/(n>1?n-1:1);
  const ri=RI_TABLE[n-1]||1.49;
  const cr=ri>0?ci/ri:0;
  return {w,lmax,ci,cr};
}

function buildMat(vals, n) {
  const mat=[];
  for(let i=0;i<n;i++) for(let j=0;j<n;j++){
    if(i===j) mat.push(1);
    else if(i<j) mat.push(Number(vals[`${i},${j}`])||1);
    else mat.push(1/(Number(vals[`${j},${i}`])||1));
  }
  return mat;
}

// ── STYLES ───────────────────────────────────────────────────────────────────
const S = {
  app:{fontFamily:"'IBM Plex Sans',system-ui,sans-serif",minHeight:"100vh",background:"#F7F8FA",color:"#1a1a2e"},
  header:{background:"#0F4C81",color:"#fff",padding:"0 32px",display:"flex",alignItems:"center",gap:16,height:56,position:"sticky",top:0,zIndex:100},
  headerTitle:{fontFamily:"'IBM Plex Mono',monospace",fontSize:15,fontWeight:600,letterSpacing:".04em"},
  headerSub:{fontSize:12,opacity:.7,marginLeft:"auto"},
  stepper:{display:"flex",gap:0,background:"#fff",borderBottom:"1px solid #e5e8ed",padding:"0 32px",overflowX:"auto"},
  step:(active,done)=>({padding:"14px 20px",fontSize:12,fontWeight:500,cursor:"pointer",borderBottom:done?"3px solid #1a7c3e":active?"3px solid #0F4C81":"3px solid transparent",color:done?"#1a7c3e":active?"#0F4C81":"#6b7280",whiteSpace:"nowrap",background:"transparent",border:"none",display:"flex",gap:6,alignItems:"center"}),
  body:{maxWidth:960,margin:"0 auto",padding:"28px 24px"},
  card:{background:"#fff",borderRadius:10,border:"1px solid #e5e8ed",padding:24,marginBottom:20},
  cardTitle:{fontSize:14,fontWeight:600,color:"#0F4C81",marginBottom:16,display:"flex",gap:8,alignItems:"center"},
  label:{fontSize:12,fontWeight:500,color:"#4b5563",marginBottom:4,display:"block"},
  input:{width:"100%",padding:"8px 12px",borderRadius:6,border:"1px solid #d1d5db",fontSize:13,outline:"none",background:"#fff",color:"#1a1a2e"},
  textarea:{width:"100%",padding:"8px 12px",borderRadius:6,border:"1px solid #d1d5db",fontSize:13,outline:"none",background:"#fff",color:"#1a1a2e",resize:"vertical"},
  btn:(variant)=>({padding:variant==="sm"?"6px 14px":"10px 22px",borderRadius:6,border:"none",cursor:"pointer",fontSize:variant==="sm"?12:13,fontWeight:500,
    background:variant==="primary"?"#0F4C81":variant==="success"?"#1a7c3e":variant==="danger"?"#b82020":variant==="ghost"?"transparent":"#f3f4f6",
    color:variant==="primary"||variant==="success"||variant==="danger"?"#fff":variant==="ghost"?"#6b7280":"#374151",
    border:variant==="ghost"?"1px solid #d1d5db":"none"}),
  grid2:{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16},
  grid3:{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12},
  table:{width:"100%",borderCollapse:"collapse",fontSize:12},
  th:{background:"#0F4C81",color:"#fff",padding:"7px 10px",textAlign:"center",fontWeight:500},
  thL:{background:"#0F4C81",color:"#fff",padding:"7px 10px",textAlign:"left",fontWeight:500},
  td:{padding:"6px 10px",textAlign:"center",border:"1px solid #e5e8ed"},
  tdL:{padding:"6px 10px",textAlign:"left",border:"1px solid #e5e8ed",fontSize:11,color:"#374151"},
  tdDiag:{padding:"6px 10px",textAlign:"center",border:"1px solid #e5e8ed",background:"#f3f4f6",color:"#6b7280",fontWeight:600},
  numInput:{width:52,padding:"3px 6px",border:"1px solid #d1d5db",borderRadius:4,fontSize:11,textAlign:"center",background:"#fff"},
  badge:(c)=>({display:"inline-block",padding:"2px 8px",borderRadius:4,fontSize:11,fontWeight:500,...(c==="ok"?{background:"#D5E8D4",color:"#1a5e2a"}:c==="warn"?{background:"#FFF0CC",color:"#7a4f00"}:{background:"#FADBD8",color:"#7a1010"})}),
  bar:{height:8,borderRadius:4,background:"#0F4C81",transition:"width .4s"},
  barWrap:{height:8,background:"#e5e8ed",borderRadius:4,overflow:"hidden",flex:1},
  winCard:{border:"2px solid #1a7c3e",borderRadius:8,background:"#f0faf4",padding:16,marginBottom:10},
  altCard:{border:"1px solid #e5e8ed",borderRadius:8,padding:16,marginBottom:10},
  note:{fontSize:11,color:"#6b7280",lineHeight:1.6},
  sec:{fontSize:13,fontWeight:600,color:"#374151",marginBottom:12,marginTop:20,paddingBottom:6,borderBottom:"1px solid #e5e8ed"},
  loading:{display:"flex",flexDirection:"column",gap:8,alignItems:"center",padding:40,color:"#6b7280",fontSize:13},
};

const SCALEROWS=[
  ["Igualmente preferido","1"],["Moderadamente preferido","3"],
  ["Fuertemente preferido","5"],["Muy fuertemente preferido","7"],
  ["Extremadamente preferido","9"],["Valores intermedios","2,4,6,8"]
];

const DEFAULT_CRITERIA=["CAPEX","OPEX","Tiempos de ejecución obras","Confiabilidad","Mantenibilidad","Impacto ambiental","Flexibilidad","Constructibilidad"];
const DEFAULT_ALTS=["Alternativa 1","Alternativa 2","Alternativa 3"];

// ── VALORES DEMO ──────────────────────────────────────────────────────────────
const DEMO_PROJ = {
  nombre:"Selección sistema de conexión S/E El Porvenir 115 kV",
  cliente:"EPM S.A. E.S.P.",
  codigo:"MEMC-EH-2026-001",
  revision:"01",
  fecha:new Date().toISOString().slice(0,10),
  elaboro:"Ing. Juan David Marín J.",
  reviso:"Ing. Director de Proyectos",
  aprobo:"Gerente Técnico",
  objeto:"Seleccionar la alternativa óptima de conexión para la nueva subestación El Porvenir 115/13.2 kV mediante análisis multicriterio AHP (Saaty, 1980), garantizando el cumplimiento de la RETIE 2024 y los criterios técnico-económicos del proyecto.",
  alcance:"Evaluación de tres alternativas de conexión: línea aérea, cable subterráneo y sistema híbrido. Se consideran criterios económicos, técnicos, ambientales y constructivos para un proyecto en zona urbana de Bogotá D.C.",
  antecedentes:"EPM requiere reforzar la red de distribución en la zona norte de Bogotá con una nueva S/E 115/13.2 kV, 2×50 MVA. El corredor disponible cruza zonas residenciales y áreas protegidas, lo que exige un análisis multicriterio para justificar la decisión ante la CREG y las comunidades."
};

const DEMO_CRITERIA = [
  {name:"CAPEX",              desc:"Costo de inversión inicial: obra civil, equipos, materiales e instalación (MUSD)"},
  {name:"OPEX",               desc:"Costos de operación y mantenimiento anual durante vida útil de 30 años (MUSD/año)"},
  {name:"Tiempos de ejecución obras", desc:"Duración total del cronograma de construcción y puesta en servicio (meses)"},
  {name:"Confiabilidad",      desc:"SAIDI/SAIFI esperado, redundancia N-1 y disponibilidad del sistema (%)"},
  {name:"Mantenibilidad",     desc:"Facilidad de inspección, acceso para mantenimiento predictivo y correctivo"},
  {name:"Impacto ambiental",  desc:"Afectación a zonas protegidas, huella de carbono y percepción comunitaria"},
  {name:"Flexibilidad",       desc:"Capacidad de expansión futura y adaptación a cambios de carga o trazado"},
  {name:"Constructibilidad",  desc:"Facilidad constructiva, disponibilidad de contratistas especializados y riesgo técnico"},
];

// Matriz AHP criterios — triángulo superior (i < j): valor = importancia fila vs columna
const DEMO_CRIT_VALS = {
  "0,1":2,   "0,2":3,   "0,3":0.5, "0,4":4,   "0,5":7,   "0,6":5,   "0,7":2,
             "1,2":2,   "1,3":0.5, "1,4":3,   "1,5":5,   "1,6":4,   "1,7":1,
                        "2,3":0.25,"2,4":2,   "2,5":3,   "2,6":3,   "2,7":0.5,
                                   "3,4":5,   "3,5":9,   "3,6":7,   "3,7":3,
                                              "4,5":3,   "4,6":2,   "4,7":0.5,
                                                         "5,6":1,   "5,7":0.5,
                                                                    "6,7":0.5,
};

const DEMO_ALTS = [
  {name:"Línea aérea 115 kV",     desc:"Torre metálica ACSR – trazado aéreo por corredor existente. Menor CAPEX, mayor velocidad de ejecución."},
  {name:"Cable subterráneo 115 kV",desc:"Cable XLPE 115 kV enterrado en zanja – trazado urbano subterráneo. Mayor confiabilidad y menor impacto visual."},
  {name:"Sistema híbrido",         desc:"Combinación aéreo-subterráneo: tramos aéreos en zonas rurales y subterráneo al ingresar al área urbana."},
];

// Matrices AHP por criterio [0..7]: triángulo superior alternativas (0=Aérea, 1=Subterráneo, 2=Híbrido)
const DEMO_ALT_VALS = {
  0: {"0,1":5,    "0,2":3,    "1,2":0.5},  // CAPEX: Aérea mucho más económica
  1: {"0,1":0.33, "0,2":0.5,  "1,2":2  },  // OPEX: Subterráneo menor mantenimiento
  2: {"0,1":3,    "0,2":2,    "1,2":0.5},  // Tiempos: Aérea más rápida
  3: {"0,1":0.25, "0,2":0.5,  "1,2":3  },  // Confiabilidad: Subterráneo superior
  4: {"0,1":4,    "0,2":2,    "1,2":0.5},  // Mantenibilidad: Aérea más accesible
  5: {"0,1":0.33, "0,2":0.5,  "1,2":2  },  // Impacto ambiental: Subterráneo menos impacto
  6: {"0,1":1,    "0,2":0.5,  "1,2":0.5},  // Flexibilidad: Híbrido más flexible
  7: {"0,1":3,    "0,2":2,    "1,2":0.5},  // Constructibilidad: Aérea más simple
};

// ── MAIN APP ─────────────────────────────────────────────────────────────────
export default function MEMCApp() {
  const [step, setStep] = useState(0);

  // Step 0 — Proyecto
  const [proj, setProj] = useState(DEMO_PROJ);

  // Step 1 — Criterios
  const [criteria, setCriteria] = useState(DEMO_CRITERIA);
  const [critVals, setCritVals] = useState(DEMO_CRIT_VALS);

  // Step 2 — Alternativas
  const [alts, setAlts] = useState(DEMO_ALTS);
  const [altVals, setAltVals] = useState(DEMO_ALT_VALS);

  // Step 3 — Resultados + Informe
  const [results, setResults] = useState(null);
  const [report, setReport] = useState("");
  const [loadingReport, setLoadingReport] = useState(false);
  const [reportError, setReportError] = useState("");

  const STEPS = ["Datos del Proyecto","Criterios y Pesos","Evaluación Alternativas","Resultados e Informe"];

  // ── COMPUTAR AHP ─────────────────────────────────────────────────────────
  function compute() {
    const nc=criteria.length, na=alts.length;
    const cm=buildMat(critVals,nc);
    const {w:cw,lmax:clmax,ci:cci,cr:ccr}=ahp(cm,nc);

    const altResults={};
    criteria.forEach((c,ci)=>{
      const am=buildMat(altVals[ci]||{},na);
      const {w,lmax,ci:ai,cr}=ahp(am,na);
      altResults[ci]={w,lmax,ci:ai,cr};
    });

    const scores=alts.map((_,ai)=>{
      let s=0; criteria.forEach((_,ci)=>s+=cw[ci]*(altResults[ci].w[ai]||0));
      return s;
    });
    const maxS=Math.max(...scores);
    const winner=scores.indexOf(maxS);
    setResults({cw,clmax,cci,ccr,altResults,scores,winner,nc,na});
    setStep(3);
  }

  // ── GENERAR INFORME DE REFERENCIA (sin IA) ──────────────────────────────
  function generateReport() {
    if(!results) return;
    const {cw,clmax,cci,ccr,altResults,scores,winner}=results;
    const nc=criteria.length, na=alts.length;
    const winnerName=alts[winner].name;
    const ranked=[...alts.map((a,i)=>({a,s:scores[i],i}))].sort((x,y)=>y.s-x.s);
    const fmt=v=>(v*100).toFixed(1)+"%";
    const fmtN=v=>v.toFixed(3);

    const lines=[];
    const h2=t=>lines.push(`## ${t}`);
    const h3=t=>lines.push(`### ${t}`);
    const p=t=>lines.push(t);
    const br=()=>lines.push("");

    // ── 1. RESUMEN EJECUTIVO ────────────────────────────────────────────────
    h2("1. Resumen Ejecutivo");
    p(`El presente documento constituye la Memoria de Evaluación Multicriterio (MEMC) para el proyecto "${proj.nombre||"(sin especificar)"}", desarrollado para ${proj.cliente||"(cliente no especificado)"}. Mediante la aplicación del Proceso Analítico Jerárquico (AHP) propuesto por Saaty (1980), se evaluaron ${na} alternativas bajo ${nc} criterios técnicos, económicos y ambientales.`);
    br();
    p(`La evaluación determinó como alternativa óptima **${winnerName}** con una puntuación ponderada del **${fmt(scores[winner])}**, superando a ${ranked.slice(1).map(r=>`${r.a.name} (${fmt(r.s)})`).join(" y ")}. La razón de consistencia de la matriz de criterios fue RC = ${fmt(ccr)}, ${ccr<=0.1?"dentro del límite de aceptabilidad del 10% establecido por Saaty (1980), lo que valida la coherencia de los juicios emitidos.":"superior al límite del 10% recomendado por Saaty — se recomienda revisar los juicios de comparación."}`);
    br();

    // ── 2. GENERALIDADES ───────────────────────────────────────────────────
    h2("2. Generalidades");
    h3("2.1. Objeto del documento");
    p(proj.objeto||"Seleccionar la alternativa técnicamente óptima mediante análisis multicriterio con metodología AHP (Saaty, 1980), garantizando objetividad, trazabilidad y cumplimiento de la normativa técnica aplicable.");
    br();

    h3("2.2. Alcance");
    p(proj.alcance||`Evaluación de ${na} alternativas de diseño bajo ${nc} criterios ponderados. El análisis cubre aspectos económicos, técnicos, ambientales y constructivos, conforme a los requisitos del cliente y la normativa RETIE 2024 (Res. 40117/2024) y NTC 2050.`);
    br();

    h3("2.3. Introducción");
    p(proj.antecedentes||`La selección de alternativas en proyectos de infraestructura eléctrica requiere un enfoque sistemático que integre múltiples criterios de decisión. La metodología MEMC-AHP permite descomponer el problema en una jerarquía de criterios y alternativas, asignando pesos numéricos mediante comparaciones por pares, lo que reduce la subjetividad inherente a las decisiones de ingeniería.`);
    br();

    // ── 3. METODOLOGÍA ─────────────────────────────────────────────────────
    h2("3. Metodología");
    h3("3.1. Criterios de evaluación");
    p(`Para la presente evaluación se definieron ${nc} criterios de análisis:`);
    br();
    criteria.forEach((c,i)=>{
      p(`- **${c.name}** (peso AHP: ${fmt(cw[i])}): ${c.desc||"Criterio de evaluación técnica-económica."}`);
    });
    br();

    h3("3.2. Escalas de ponderación y normalización (AHP-Saaty)");
    p("La comparación por pares utiliza la escala de Saaty (1980), que asigna valores del 1 al 9:");
    br();
    p("- **1** — Igualmente preferido: ambos elementos contribuyen por igual al objetivo.");
    p("- **3** — Moderadamente preferido: experiencia y juicio levemente favorecen un elemento.");
    p("- **5** — Fuertemente preferido: experiencia y juicio favorecen claramente un elemento.");
    p("- **7** — Muy fuertemente preferido: un elemento es favorecido con fuerte evidencia.");
    p("- **9** — Extremadamente preferido: la evidencia que favorece un elemento es del más alto grado.");
    p("- **2, 4, 6, 8** — Valores intermedios entre juicios adyacentes.");
    br();
    p("Los valores recíprocos (1/3, 1/5, etc.) se aplican automáticamente en la mitad inferior de la matriz. Los vectores de prioridad se obtienen normalizando la matriz y promediando por filas.");
    br();

    h3("3.3. Consistencia en el Proceso Analítico Jerárquico");
    p("La validez de los juicios emitidos se verifica mediante los indicadores de consistencia de Saaty:");
    br();
    p("- **λmax (valor propio principal):** indica la consistencia global de la matriz. Para una matriz perfectamente consistente, λmax = n (número de criterios).");
    p("- **IC (Índice de Consistencia):** IC = (λmax − n) / (n − 1). Mide la desviación respecto a la consistencia perfecta.");
    p("- **RC (Razón de Consistencia):** RC = IC / IR, donde IR es el Índice Aleatorio de Saaty. Se acepta RC ≤ 10%.");
    br();
    p(`Para la matriz de criterios del presente estudio: λmax = ${fmtN(clmax)}, IC = ${fmtN(cci)}, RC = ${fmt(ccr)} → ${ccr<=0.1?"**CONSISTENTE** ✓":"**REQUIERE REVISIÓN** ✗"}.`);
    br();

    // ── 4. RESULTADOS ──────────────────────────────────────────────────────
    h2("4. Resultados de la Evaluación Multicriterio");
    h3("4.1. Ponderación de criterios");
    p("El vector de prioridad AHP arrojó los siguientes pesos para cada criterio, ordenados de mayor a menor importancia:");
    br();
    [...criteria.map((c,i)=>({c,w:cw[i],i}))]
      .sort((a,b)=>b.w-a.w)
      .forEach(({c,w},rank)=>{
        p(`- **${c.name}:** ${fmt(w)}${rank===0?" ← criterio dominante":""}`);
      });
    br();
    p(`El criterio con mayor influencia en la decisión es **${criteria[cw.indexOf(Math.max(...cw))].name}** (${fmt(Math.max(...cw))}), lo que refleja la prioridad del equipo evaluador hacia este aspecto del proyecto.`);
    br();

    h3("4.2. Evaluación de alternativas por criterio");
    criteria.forEach((c,ci)=>{
      const {w,cr}=altResults[ci];
      const maxW=Math.max(...w);
      const bestAlt=alts[w.indexOf(maxW)].name;
      p(`**${c.name}** (peso: ${fmt(cw[ci])}) — RC: ${fmt(cr)} ${cr<=0.1?"✓":"✗"}`);
      alts.forEach((a,ai)=>p(`  - ${a.name}: ${fmt(w[ai])}${w[ai]===maxW?" ← mejor en este criterio":""}`));
      p(`  Mejor desempeño: ${bestAlt}.`);
      br();
    });

    h3("4.3. Resultado final y selección");
    p("La puntuación ponderada final de cada alternativa, integrando todos los criterios, es:");
    br();
    ranked.forEach(({a,s,i},rank)=>{
      p(`- **#${rank+1} ${a.name}:** ${fmt(s)}${i===winner?" ← **ALTERNATIVA SELECCIONADA**":""}`);
    });
    br();
    p(`La alternativa **${winnerName}** obtiene la mayor puntuación ponderada (${fmt(scores[winner])}), consolidando su desempeño superior en los criterios de mayor peso. Esta selección garantiza el balance óptimo entre los objetivos técnicos, económicos y ambientales del proyecto.`);
    br();

    // ── 5. CONCLUSIONES ────────────────────────────────────────────────────
    h2("5. Conclusiones y Recomendaciones");
    p(`1. La aplicación del método AHP (Saaty, 1980) permitió evaluar de manera objetiva y trazable ${na} alternativas bajo ${nc} criterios ponderados, obteniendo resultados con RC = ${fmt(ccr)} ${ccr<=0.1?"(consistencia aceptable).":"(se recomienda revisar los juicios de comparación)."}`);
    br();
    p(`2. La alternativa seleccionada es **${winnerName}** con un índice de desempeño ponderado del **${fmt(scores[winner])}**, lo que representa una ventaja de ${(scores[winner]-scores[ranked[1].i])*100>0?((scores[winner]-scores[ranked[1].i])*100).toFixed(1):0} puntos porcentuales frente a la segunda mejor opción (${ranked[1].a.name}, ${fmt(ranked[1].s)}).`);
    br();
    p(`3. Se recomienda implementar la alternativa seleccionada conforme a los requisitos de la RETIE 2024 (Resolución 40117 de 2024), NTC 2050 y los lineamientos del Operador de Red correspondiente.`);
    br();
    p(`4. Para futuros estudios, se sugiere ampliar el número de criterios con análisis de riesgo cuantitativo (Monte Carlo) y sensibilidad de pesos, con el fin de robustecer la decisión ante incertidumbres en los insumos de evaluación.`);
    br();

    // ── 6. REFERENCIAS ─────────────────────────────────────────────────────
    h2("6. Referencias Bibliográficas");
    p("- Saaty, T. L. (1980). *The Analytic Hierarchy Process: Planning, Priority Setting, Resource Allocation*. McGraw-Hill.");
    p("- Saaty, T. L. (2008). Decision making with the analytic hierarchy process. *Int. J. Services Sciences*, 1(1), 83–98.");
    p("- Ministerio de Minas y Energía (2024). *Reglamento Técnico de Instalaciones Eléctricas — RETIE 2024* (Resolución 40117 de 2024). Bogotá.");
    p("- ICONTEC (1998). *NTC 2050: Código Eléctrico Colombiano*. Bogotá.");
    p("- ISO 55000:2014. *Asset management — Overview, principles and terminology*. International Organization for Standardization.");
    p("- CREG (vigente). Resoluciones aplicables al proyecto según el nivel de tensión y el operador de red.");

    setReport(lines.join("\n"));
  }

  function copyReport(){
    navigator.clipboard.writeText(report).catch(()=>{});
    alert("Informe copiado al portapapeles");
  }

  function downloadReport(){
    const fecha = proj.fecha || new Date().toISOString().slice(0,10);

    // Convierte markdown a HTML con estilos Word
    const toHtml = txt => txt.split("\n").map(line => {
      if(line.startsWith("## "))  return `<h2>${line.slice(3).replace(/\*\*(.*?)\*\*/g,"<strong>$1</strong>")}</h2>`;
      if(line.startsWith("### ")) return `<h3>${line.slice(4).replace(/\*\*(.*?)\*\*/g,"<strong>$1</strong>")}</h3>`;
      if(line.startsWith("- "))   return `<li>${line.slice(2).replace(/\*\*(.*?)\*\*/g,"<strong>$1</strong>").replace(/\*(.*?)\*/g,"<em>$1</em>")}</li>`;
      if(!line.trim())             return "<br/>";
      return `<p>${line.replace(/\*\*(.*?)\*\*/g,"<strong>$1</strong>").replace(/\*(.*?)\*/g,"<em>$1</em>")}</p>`;
    }).join("\n");

    const doc = `<html xmlns:o='urn:schemas-microsoft-com:office:office'
      xmlns:w='urn:schemas-microsoft-com:office:word'>
<head><meta charset='utf-8'>
<style>
  body  { font-family:Calibri,sans-serif; font-size:11pt; margin:2.5cm; color:#1a1a2e; }
  h1    { font-size:15pt; color:#0F4C81; margin-bottom:4pt; }
  h2    { font-size:13pt; color:#0F4C81; border-bottom:1px solid #b0c4de;
          padding-bottom:3pt; margin-top:18pt; margin-bottom:6pt; }
  h3    { font-size:11pt; color:#374151; margin-top:12pt; margin-bottom:4pt; }
  p, li { font-size:11pt; line-height:1.6; margin:3pt 0; }
  li    { margin-left:18pt; }
  .cover{ border-bottom:3px solid #0F4C81; padding-bottom:14pt; margin-bottom:20pt; }
  .meta { font-size:9pt; color:#6b7280; }
  .tag  { font-size:8pt; color:#888; text-transform:uppercase; letter-spacing:1pt; }
</style></head>
<body>
<div class="cover">
  <div class="tag">Documento Técnico — MEMC · ElectroHub Ingeniería S.A.S.</div>
  <h1>${proj.nombre||"MATRIZ DE EVALUACIÓN MULTICRITERIO"}</h1>
  <p class="meta">Código: ${proj.codigo||"MEMC-001"} &nbsp;|&nbsp; Rev: ${proj.revision} &nbsp;|&nbsp; Fecha: ${fecha}</p>
  ${proj.cliente?`<p class="meta">Cliente: <strong>${proj.cliente}</strong></p>`:""}
  <p class="meta">
    ${proj.elaboro?`Elaboró: ${proj.elaboro} &nbsp;&nbsp;`:""}
    ${proj.reviso?`Revisó: ${proj.reviso} &nbsp;&nbsp;`:""}
    ${proj.aprobo?`Aprobó: ${proj.aprobo}`:""}
  </p>
</div>
${toHtml(report)}
</body></html>`;

    const blob = new Blob(["\ufeff"+doc], {type:"application/msword"});
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href     = url;
    a.download = `MEMC_${(proj.codigo||"informe").replace(/[^a-zA-Z0-9\-_]/g,"_")}_${fecha}.doc`;
    a.click();
    setTimeout(()=>URL.revokeObjectURL(url), 3000);
  }

  // ── RENDER STEP 0 ────────────────────────────────────────────────────────
  function renderStep0() {
    const f=(k,label,multi)=>(
      <div key={k} style={{marginBottom:14}}>
        <label style={S.label}>{label}</label>
        {multi
          ? <textarea rows={3} style={S.textarea} value={proj[k]} onChange={e=>setProj(p=>({...p,[k]:e.target.value}))}/>
          : <input style={S.input} value={proj[k]} onChange={e=>setProj(p=>({...p,[k]:e.target.value}))}/>}
      </div>
    );
    return (
      <div>
        <div style={S.card}>
          <div style={S.cardTitle}>📋 Identificación del Proyecto</div>
          <div style={S.grid2}>
            {f("nombre","Nombre del proyecto")}
            {f("cliente","Cliente / Empresa")}
            {f("codigo","Código del documento")}
            {f("revision","Revisión")}
            {f("fecha","Fecha")}
          </div>
        </div>
        <div style={S.card}>
          <div style={S.cardTitle}>👥 Control de Emisión</div>
          <div style={S.grid3}>
            {f("elaboro","Elaboró")}
            {f("reviso","Revisó")}
            {f("aprobo","Aprobó")}
          </div>
        </div>
        <div style={S.card}>
          <div style={S.cardTitle}>📄 Descripción del Proyecto</div>
          {f("objeto","Objeto del documento",true)}
          {f("alcance","Alcance",true)}
          {f("antecedentes","Antecedentes del proyecto",true)}
        </div>
        <div style={{display:"flex",justifyContent:"flex-end"}}>
          <button style={S.btn("primary")} onClick={()=>setStep(1)}>Continuar →</button>
        </div>
      </div>
    );
  }

  // ── RENDER STEP 1 ────────────────────────────────────────────────────────
  function renderStep1() {
    const nc=criteria.length;
    const mat=buildMat(critVals,nc);
    const {w,lmax,ci,cr}=ahp(mat,nc);

    const addCrit=()=>{if(criteria.length<10)setCriteria(c=>[...c,{name:"Criterio "+(c.length+1),desc:""}])};
    const delCrit=(i)=>{if(criteria.length>3)setCriteria(c=>c.filter((_,j)=>j!==i))};

    return (
      <div>
        <div style={S.card}>
          <div style={S.cardTitle}>⚖️ Escala de Saaty (1980)</div>
          <table style={S.table}>
            <thead><tr><th style={S.thL}>Preferencia relativa</th><th style={S.th}>Valor</th></tr></thead>
            <tbody>{SCALEROWS.map(([l,v])=>(
              <tr key={l}><td style={S.tdL}>{l}</td><td style={S.td}>{v}</td></tr>
            ))}</tbody>
          </table>
        </div>

        <div style={S.card}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
            <div style={S.cardTitle}>🎯 Criterios de Evaluación ({nc})</div>
            <button style={S.btn("sm")} onClick={addCrit}>+ Agregar criterio</button>
          </div>
          {criteria.map((c,i)=>(
            <div key={i} style={{display:"flex",gap:8,marginBottom:8,alignItems:"flex-start"}}>
              <div style={{flex:1}}>
                <input style={{...S.input,marginBottom:4}} placeholder="Nombre del criterio" value={c.name}
                  onChange={e=>setCriteria(cr=>cr.map((x,j)=>j===i?{...x,name:e.target.value}:x))}/>
                <input style={{...S.input,fontSize:11,color:"#6b7280"}} placeholder="Descripción (opcional)"
                  value={c.desc} onChange={e=>setCriteria(cr=>cr.map((x,j)=>j===i?{...x,desc:e.target.value}:x))}/>
              </div>
              {criteria.length>3&&<button style={{...S.btn("danger"),padding:"4px 10px",fontSize:11,marginTop:2}} onClick={()=>delCrit(i)}>✕</button>}
            </div>
          ))}
        </div>

        <div style={S.card}>
          <div style={S.cardTitle}>📊 Matriz de Comparación por Pares (Criterios)</div>
          <p style={{...S.note,marginBottom:12}}>Complete solo la mitad superior. La recíproca se calcula automáticamente. Valores: 1–9 (escala Saaty).</p>
          <div style={{overflowX:"auto"}}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={{...S.thL,minWidth:120}}>Criterio</th>
                  {criteria.map((c,i)=><th key={i} style={{...S.th,minWidth:60,fontSize:10}}>{c.name}</th>)}
                </tr>
              </thead>
              <tbody>
                {criteria.map((cr,i)=>(
                  <tr key={i}>
                    <td style={{...S.tdL,fontWeight:500}}>{cr.name}</td>
                    {criteria.map((_,j)=>{
                      if(i===j) return <td key={j} style={S.tdDiag}>1</td>;
                      if(i>j){
                        const v=critVals[`${j},${i}`]||1;
                        return <td key={j} style={{...S.td,color:"#9ca3af",fontSize:10}}>{v===1?"1":v>1?"1/"+v:+(1/v).toFixed(2)}</td>;
                      }
                      return <td key={j} style={S.td}>
                        <input type="number" min={1} max={9} step={1} style={S.numInput}
                          value={critVals[`${i},${j}`]||1}
                          onChange={e=>{const v=Math.max(1,Math.min(9,+e.target.value||1));
                            setCritVals(p=>({...p,[`${i},${j}`]:v}));}}/>
                      </td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div style={S.card}>
          <div style={S.cardTitle}>📈 Vectores de Prioridad y Consistencia</div>
          <div style={S.grid3}>
            <div style={{background:"#f3f4f6",borderRadius:6,padding:12,textAlign:"center"}}>
              <div style={{fontSize:20,fontWeight:600}}>{lmax.toFixed(3)}</div>
              <div style={{fontSize:11,color:"#6b7280",marginTop:2}}>λ máx</div>
            </div>
            <div style={{background:"#f3f4f6",borderRadius:6,padding:12,textAlign:"center"}}>
              <div style={{fontSize:20,fontWeight:600}}>{ci.toFixed(3)}</div>
              <div style={{fontSize:11,color:"#6b7280",marginTop:2}}>Índice CI</div>
            </div>
            <div style={{background:cr<=0.1?"#D5E8D4":"#FADBD8",borderRadius:6,padding:12,textAlign:"center"}}>
              <div style={{fontSize:18,fontWeight:600,color:cr<=0.1?"#1a5e2a":"#7a1010"}}>{(cr*100).toFixed(1)}%</div>
              <div style={{fontSize:11,color:cr<=0.1?"#1a5e2a":"#7a1010",marginTop:2}}>RC {cr<=0.1?"✓ consistente":"✗ revisar"}</div>
            </div>
          </div>
          <div style={{marginTop:16}}>
            <table style={S.table}>
              <thead><tr>
                <th style={S.thL}>Criterio</th>
                <th style={S.th}>Peso AHP</th>
                <th style={S.th}>%</th>
                <th style={{...S.th,minWidth:120}}>Distribución</th>
              </tr></thead>
              <tbody>{[...criteria.map((c,i)=>({c,w:w[i],i}))]
                .sort((a,b)=>b.w-a.w)
                .map(({c,w:wi,i})=>(
                <tr key={i}>
                  <td style={S.tdL}>{c.name}</td>
                  <td style={S.td}>{wi.toFixed(4)}</td>
                  <td style={{...S.td,fontWeight:500}}>{(wi*100).toFixed(1)}%</td>
                  <td style={S.td}>
                    <div style={S.barWrap}><div style={{...S.bar,width:`${(wi*100).toFixed(1)}%`}}/></div>
                  </td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </div>

        <div style={{display:"flex",justifyContent:"space-between"}}>
          <button style={S.btn("ghost")} onClick={()=>setStep(0)}>← Atrás</button>
          <button style={S.btn("primary")} onClick={()=>setStep(2)}>Continuar →</button>
        </div>
      </div>
    );
  }

  // ── RENDER STEP 2 ────────────────────────────────────────────────────────
  function renderStep2() {
    const na=alts.length;
    const addAlt=()=>{if(alts.length<6)setAlts(a=>[...a,{name:"Alternativa "+(a.length+1),desc:""}])};
    const delAlt=(i)=>{if(alts.length>2)setAlts(a=>a.filter((_,j)=>j!==i))};

    return (
      <div>
        <div style={S.card}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
            <div style={S.cardTitle}>🔀 Alternativas de Diseño ({na})</div>
            <button style={S.btn("sm")} onClick={addAlt}>+ Agregar alternativa</button>
          </div>
          {alts.map((a,i)=>(
            <div key={i} style={{display:"flex",gap:8,marginBottom:8,alignItems:"flex-start"}}>
              <div style={{flex:1}}>
                <input style={{...S.input,marginBottom:4}} placeholder="Nombre de la alternativa" value={a.name}
                  onChange={e=>setAlts(al=>al.map((x,j)=>j===i?{...x,name:e.target.value}:x))}/>
                <input style={{...S.input,fontSize:11,color:"#6b7280"}} placeholder="Descripción (opcional)"
                  value={a.desc} onChange={e=>setAlts(al=>al.map((x,j)=>j===i?{...x,desc:e.target.value}:x))}/>
              </div>
              {alts.length>2&&<button style={{...S.btn("danger"),padding:"4px 10px",fontSize:11,marginTop:2}} onClick={()=>delAlt(i)}>✕</button>}
            </div>
          ))}
        </div>

        {criteria.map((crit,ci)=>{
          const vals=altVals[ci]||{};
          const mat=buildMat(vals,na);
          const {w,cr}=ahp(mat,na);
          return (
            <div key={ci} style={S.card}>
              <div style={S.cardTitle}>📐 Criterio: {crit.name}</div>
              <div style={{overflowX:"auto",marginBottom:12}}>
                <table style={S.table}>
                  <thead>
                    <tr>
                      <th style={{...S.thL,minWidth:100}}>Alternativa</th>
                      {alts.map((a,i)=><th key={i} style={{...S.th,fontSize:10,maxWidth:90}}>{a.name}</th>)}
                      <th style={S.th}>Peso %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {alts.map((alt,ai)=>(
                      <tr key={ai}>
                        <td style={{...S.tdL,fontSize:11}}>{alt.name}</td>
                        {alts.map((_,aj)=>{
                          if(ai===aj) return <td key={aj} style={S.tdDiag}>1</td>;
                          if(ai>aj){
                            const v=vals[`${aj},${ai}`]||1;
                            return <td key={aj} style={{...S.td,color:"#9ca3af",fontSize:10}}>{v===1?"1":v>1?"1/"+v:+(1/v).toFixed(2)}</td>;
                          }
                          return <td key={aj} style={S.td}>
                            <input type="number" min={1} max={9} step={1} style={S.numInput}
                              value={vals[`${ai},${aj}`]||1}
                              onChange={e=>{const v=Math.max(1,Math.min(9,+e.target.value||1));
                                setAltVals(p=>({...p,[ci]:{...p[ci],[`${ai},${aj}`]:v}}));}}/>
                          </td>;
                        })}
                        <td style={{...S.td,fontWeight:600,color:"#0F4C81"}}>{(w[ai]*100).toFixed(1)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{display:"flex",gap:8,alignItems:"center"}}>
                <span style={S.note}>RC: </span>
                <span style={S.badge(cr<=0.1?"ok":"err")}>
                  {(cr*100).toFixed(1)}% {cr<=0.1?"✓":"✗ revisar"}
                </span>
              </div>
            </div>
          );
        })}

        <div style={{display:"flex",justifyContent:"space-between"}}>
          <button style={S.btn("ghost")} onClick={()=>setStep(1)}>← Atrás</button>
          <button style={S.btn("primary")} onClick={compute}>Calcular resultados →</button>
        </div>
      </div>
    );
  }

  // ── RENDER STEP 3 ────────────────────────────────────────────────────────
  function renderStep3() {
    if(!results) return <div style={S.note}>Vuelva al paso anterior y calcule los resultados.</div>;
    const {cw,clmax,cci,ccr,altResults,scores,winner,nc,na}=results;

    // Render report as formatted HTML
    function renderMarkdown(txt){
      if(!txt) return null;
      return txt.split("\n").map((line,i)=>{
        if(line.startsWith("## ")) return <h2 key={i} style={{fontSize:15,fontWeight:600,color:"#0F4C81",margin:"20px 0 8px",paddingBottom:4,borderBottom:"1px solid #e5e8ed"}}>{line.slice(3)}</h2>;
        if(line.startsWith("### ")) return <h3 key={i} style={{fontSize:13,fontWeight:600,color:"#374151",margin:"14px 0 6px"}}>{line.slice(4)}</h3>;
        if(line.startsWith("- ")) return <p key={i} style={{fontSize:12,lineHeight:1.7,margin:"3px 0 3px 12px"}}>• {line.slice(2)}</p>;
        if(!line.trim()) return <div key={i} style={{height:6}}/>;
        return <p key={i} style={{fontSize:12,lineHeight:1.75,margin:"4px 0",color:"#374151"}}>{line}</p>;
      });
    }

    return (
      <div>
        {/* Resumen de puntuación */}
        <div style={S.card}>
          <div style={S.cardTitle}>🏆 Ranking de Alternativas</div>
          {[...alts.map((a,i)=>({a,s:scores[i],i}))].sort((x,y)=>y.s-x.s).map(({a,s,i},rank)=>(
            <div key={i} style={i===winner?S.winCard:S.altCard}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                <div style={{display:"flex",gap:8,alignItems:"center"}}>
                  <span style={{fontSize:18,fontWeight:700,color:rank===0?"#1a7c3e":"#0F4C81"}}>#{rank+1}</span>
                  <span style={{fontSize:13,fontWeight:500}}>{a.name}</span>
                  {i===winner&&<span style={S.badge("ok")}>✓ Seleccionada</span>}
                </div>
                <span style={{fontSize:16,fontWeight:600,color:rank===0?"#1a7c3e":"#0F4C81"}}>{(s*100).toFixed(1)}%</span>
              </div>
              <div style={S.barWrap}>
                <div style={{...S.bar,width:`${(s*100).toFixed(1)}%`,background:rank===0?"#1a7c3e":"#0F4C81"}}/>
              </div>
            </div>
          ))}
        </div>

        {/* Tabla detalle */}
        <div style={S.card}>
          <div style={S.cardTitle}>📊 Detalle por Criterio</div>
          <div style={{overflowX:"auto"}}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.thL}>Criterio</th>
                  <th style={S.th}>Peso</th>
                  {alts.map((a,i)=><th key={i} style={{...S.th,fontSize:10}}>{a.name}</th>)}
                </tr>
              </thead>
              <tbody>
                {criteria.map((c,ci)=>{
                  const {w}=altResults[ci];
                  const maxW=Math.max(...w);
                  return (
                    <tr key={ci}>
                      <td style={S.tdL}>{c.name}</td>
                      <td style={{...S.td,fontWeight:500}}>{(cw[ci]*100).toFixed(1)}%</td>
                      {alts.map((_,ai)=>(
                        <td key={ai} style={{...S.td,fontWeight:w[ai]===maxW?600:400,color:w[ai]===maxW?"#1a7c3e":"inherit"}}>
                          {(w[ai]*100).toFixed(1)}%
                        </td>
                      ))}
                    </tr>
                  );
                })}
                <tr style={{background:"#f3f4f6"}}>
                  <td style={{...S.tdL,fontWeight:600}}>TOTAL PONDERADO</td>
                  <td style={{...S.td,fontWeight:600}}>100%</td>
                  {alts.map((_,i)=>(
                    <td key={i} style={{...S.td,fontWeight:700,color:i===winner?"#1a7c3e":"#0F4C81",fontSize:13}}>
                      {(scores[i]*100).toFixed(1)}%
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Consistencia */}
        <div style={S.card}>
          <div style={S.cardTitle}>✅ Verificación de Consistencia</div>
          <table style={S.table}>
            <thead><tr>
              <th style={S.thL}>Matriz</th>
              <th style={S.th}>λmax</th>
              <th style={S.th}>CI</th>
              <th style={S.th}>RC</th>
              <th style={S.th}>Estado</th>
            </tr></thead>
            <tbody>
              <tr>
                <td style={S.tdL}>Criterios</td>
                <td style={S.td}>{clmax.toFixed(3)}</td>
                <td style={S.td}>{cci.toFixed(3)}</td>
                <td style={S.td}>{(ccr*100).toFixed(1)}%</td>
                <td style={S.td}><span style={S.badge(ccr<=0.1?"ok":"err")}>{ccr<=0.1?"✓ OK":"✗ Revisar"}</span></td>
              </tr>
              {criteria.map((c,ci)=>{
                const {lmax,ci:ai,cr}=altResults[ci];
                return <tr key={ci}>
                  <td style={S.tdL}>{c.name}</td>
                  <td style={S.td}>{lmax.toFixed(3)}</td>
                  <td style={S.td}>{ai.toFixed(3)}</td>
                  <td style={S.td}>{(cr*100).toFixed(1)}%</td>
                  <td style={S.td}><span style={S.badge(cr<=0.1?"ok":"err")}>{cr<=0.1?"✓ OK":"✗ Revisar"}</span></td>
                </tr>;
              })}
            </tbody>
          </table>
        </div>

        {/* Generador de informe */}
        <div style={S.card}>
          <div style={S.cardTitle}>📄 Informe Técnico MEMC</div>
          <p style={{...S.note,marginBottom:16}}>
            Genera el informe técnico de referencia con toda la información del proyecto, criterios ponderados, matrices AHP, resultados y conclusiones. Listo para revisar, ajustar y exportar.
          </p>
          <div style={{display:"flex",gap:10,marginBottom:16}}>
            <button style={S.btn("primary")} onClick={generateReport}>
              📄 Generar informe de referencia
            </button>
            {report&&<button style={S.btn("success")} onClick={copyReport}>📋 Copiar</button>}
            {report&&<button style={S.btn("success")} onClick={downloadReport}>⬇️ Descargar .doc</button>}
          </div>
          {reportError&&<div style={{padding:10,background:"#FADBD8",borderRadius:6,fontSize:12,color:"#7a1010",marginBottom:12}}>{reportError}</div>}
          {loadingReport&&(
            <div style={S.loading}>
              <div style={{fontSize:24}}>⚙️</div>
              <div>Redactando informe técnico...</div>
              <div style={{fontSize:11,color:"#9ca3af"}}>Esto puede tomar 15–30 segundos</div>
            </div>
          )}
          {report&&(
            <div style={{background:"#f9fafb",border:"1px solid #e5e8ed",borderRadius:8,padding:20,marginTop:8}}>
              {/* Portada del informe */}
              <div style={{borderBottom:"3px solid #0F4C81",paddingBottom:16,marginBottom:20}}>
                <div style={{fontSize:10,color:"#6b7280",marginBottom:4}}>DOCUMENTO TÉCNICO</div>
                <div style={{fontSize:17,fontWeight:700,color:"#0F4C81",marginBottom:4}}>{proj.nombre||"MATRIZ DE EVALUACIÓN MULTICRITERIO"}</div>
                <div style={{fontSize:12,color:"#374151",marginBottom:2}}>Código: {proj.codigo||"MEMC-001"} | Rev: {proj.revision} | Fecha: {proj.fecha}</div>
                {proj.cliente&&<div style={{fontSize:12,color:"#374151"}}>Cliente: {proj.cliente}</div>}
                <div style={{display:"flex",gap:16,marginTop:8,fontSize:11,color:"#6b7280"}}>
                  {proj.elaboro&&<span>Elaboró: {proj.elaboro}</span>}
                  {proj.reviso&&<span>Revisó: {proj.reviso}</span>}
                  {proj.aprobo&&<span>Aprobó: {proj.aprobo}</span>}
                </div>
              </div>
              <div>{renderMarkdown(report)}</div>
            </div>
          )}
        </div>

        <div style={{display:"flex",justifyContent:"space-between"}}>
          <button style={S.btn("ghost")} onClick={()=>setStep(2)}>← Atrás</button>
          <button style={S.btn("sm")} onClick={()=>{setStep(0);setResults(null);setReport("");}}>Nueva evaluación</button>
        </div>
      </div>
    );
  }

  const stepContent=[renderStep0,renderStep1,renderStep2,renderStep3];

  return (
    <div style={S.app}>
      <div style={S.header}>
        <span style={{fontSize:20}}>⚡</span>
        <span style={S.headerTitle}>ElectroHub · MEMC</span>
        <span style={S.headerSub}>Matriz de Evaluación Multicriterio — Método AHP</span>
      </div>
      <div style={S.stepper}>
        {STEPS.map((s,i)=>(
          <button key={i} style={S.step(i===step,i<step)} onClick={()=>i<step&&setStep(i)}>
            <span style={{fontSize:14}}>{i<step?"✓":i+1}</span> {s}
          </button>
        ))}
      </div>
      <div style={S.body}>
        {stepContent[step]()}
      </div>
    </div>
  );
}
